import React from 'react';
import { Box, Typography } from '@mui/material';

export interface CenteredPageProps {
  /** The brand at the top, e.g. a logo and a name. Drawn as given. */
  brand?: React.ReactNode;
  /** A short line under the brand, e.g. "Step 2 of 3". */
  eyebrow?: string;
  /** Width of the column in px. 480 by default. */
  maxWidth?: number;
  /**
   * `top` — the column starts near the top: for several steps in a row, so
   * the page does not jump as their heights change. `middle` — in the middle
   * of the screen: for one short thing to do, like opening a shared link.
   */
  align?: 'top' | 'middle';
  children: React.ReactNode;
}

/**
 * A page with no app around it: no sidebar, no header, one narrow column in
 * the middle. For signing in, verifying an email, accepting an invite, opening
 * a shared link: a step someone takes before they are in the workspace.
 *
 * Scrolls by itself and leaves room at the foot for a phone's home bar, so a
 * button at the bottom is never hidden.
 */
export function CenteredPage({ brand, eyebrow, maxWidth = 480, align = 'top', children }: CenteredPageProps) {
  return (
    <Box
      sx={(t) => ({
        minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
        // In the middle, sitting a little above centre, where the eye rests.
        ...(align === 'middle' && { justifyContent: 'center', '&::after': { content: '""', flex: '0 0 8vh' } }),
        overflowY: 'auto', WebkitOverflowScrolling: 'touch',
        px: { xs: 2, md: 4 }, pt: { xs: 3, sm: 4, md: 6 },
        pb: { xs: 'calc(48px + env(safe-area-inset-bottom, 0px))', sm: 'calc(64px + env(safe-area-inset-bottom, 0px))' },
        bgcolor: t.tokens.surface.canvas,
      })}
    >
      {brand ? <Box sx={{ mb: eyebrow ? 1.5 : 3, textAlign: 'center' }}>{brand}</Box> : null}
      {eyebrow ? (
        <Typography variant="overline" sx={(t) => ({ mb: 2.5, color: t.tokens.text.tertiary })}>{eyebrow}</Typography>
      ) : null}
      <Box sx={{ width: '100%', maxWidth }}>{children}</Box>
    </Box>
  );
}
